import { cn } from '@/lib/cn'
import { isObjectiveDone, type Giveaway } from '@/models/giveaway'

interface EntryHintProps {
  giveaway: Giveaway
  /** First-come giveaway: options claim a key rather than add entries. */
  firstEntries?: boolean
  className?: string
}

/**
 * States how entering works, and where the reader already stands.
 *
 * The Quasar screens rendered a bare list of Discord rows with a points chip on
 * each and no explanation, so it was not obvious that clicking one *is* the
 * entry — people read them as links to the server. This says so, then tracks
 * what they have already done. A first-come giveaway that needs every option
 * done says that up front, with progress, since missing one means no key.
 */
export function EntryHint({ giveaway, firstEntries = false, className }: EntryHintProps) {
  const options = giveaway.giveaway_objective
  if (options.length === 0) return null

  if (firstEntries) return <FirstComeHint giveaway={giveaway} className={className} />

  const entered = giveaway.display_my_entries > 0
  // A referral option is never finished, so it doesn't count as one left to do.
  const remaining = options.filter(
    (objective) => !objective.is_entered && !objective.is_referral,
  ).length

  const message = giveaway.has_ended
    ? 'This giveaway has closed.'
    : entered
      ? `You're in with ${giveaway.display_my_entries.toLocaleString()} entries.` +
        (remaining > 0
          ? ` Complete ${remaining} more option${remaining === 1 ? '' : 's'} to improve your odds.`
          : ' Every option completed — good luck.')
      : 'Choose an entry option below to enter. Each one adds the entries shown beside it.'

  return (
    <div
      className={cn('entry-hint', entered && 'is-in', giveaway.has_ended && 'is-closed', className)}
    >
      {message}
    </div>
  )
}

function FirstComeHint({ giveaway, className }: { giveaway: Giveaway; className?: string }) {
  const options = giveaway.giveaway_objective
  const total = options.length
  const done = options.filter(isObjectiveDone).length
  const requireAll = giveaway.require_all_objectives && total > 1
  const complete = requireAll ? done === total : done > 0

  if (giveaway.has_ended) {
    return <div className={cn('entry-hint is-closed', className)}>This giveaway has closed.</div>
  }

  return (
    <div
      className={cn('entry-hint', requireAll && 'is-require-all', complete && 'is-in', className)}
    >
      {requireAll ? (
        <>
          <div className="entry-hint-title">All {total} entry options required</div>
          {complete
            ? 'Every option completed — your key is above if any were left.'
            : `Complete every option below to claim a key. ${done} of ${total} done.`}
          <div
            className="entry-hint-progress"
            role="progressbar"
            aria-label="Entry options completed"
            aria-valuemin={0}
            aria-valuemax={total}
            aria-valuenow={done}
          >
            <span style={{ width: `${(done / total) * 100}%` }} />
          </div>
        </>
      ) : complete ? (
        'Option completed — your key is above if any were left.'
      ) : (
        'Complete any one entry option below to claim a key. First come, first served.'
      )}
    </div>
  )
}
